/**
 * Preferences Tab - Manage class scheduling preferences
 *
 * Allows setting preferences for each class (room, day, time, teacher)
 * with weighted priorities for the scheduling algorithm
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useGetClassesQuery } from '../../../api/schedulingApi';
import PreferenceManager from '../components/PreferenceManager';

interface PreferencesTabProps {
  scheduleId: string;
  studioId: string;
}

const PreferencesTab: React.FC<PreferencesTabProps> = ({
  scheduleId,
  studioId,
}) => {
  const { data: classes, isLoading } = useGetClassesQuery({
    scheduleId,
    studioId,
  });

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [selectedClassName, setSelectedClassName] = useState<string>('');

  const handleOpenManager = (classId: string, className: string) => {
    setSelectedClassId(classId);
    setSelectedClassName(className);
  };

  const handleCloseManager = () => {
    setSelectedClassId(null);
    setSelectedClassName('');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!classes || classes.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Class Preferences
        </Typography>
        <Alert severity="info">
          No classes added yet. Add classes in the Classes tab before setting
          preferences.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Class Preferences</Typography>
        <Typography variant="body2" color="text.secondary">
          Set scheduling preferences for each class
        </Typography>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Class Name</TableCell>
              <TableCell>Styles</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Age Range</TableCell>
              <TableCell align="center">Preferences</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {classes.map(classItem => {
              const styleNames = classItem.style_details
                ? classItem.style_details.map(s => s.title).join(', ')
                : classItem.styles.join(', ');

              return (
                <TableRow key={classItem.id}>
                  <TableCell>{classItem.class_name}</TableCell>
                  <TableCell>{styleNames}</TableCell>
                  <TableCell>{classItem.level}</TableCell>
                  <TableCell>{classItem.age_range_display}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label="Configure"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<TuneIcon />}
                      onClick={() =>
                        handleOpenManager(classItem.id, classItem.class_name)
                      }
                    >
                      Manage Preferences
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedClassId && (
        <PreferenceManager
          open={!!selectedClassId}
          onClose={handleCloseManager}
          classId={selectedClassId}
          className={selectedClassName}
          scheduleId={scheduleId}
          studioId={studioId}
        />
      )}
    </Box>
  );
};

export default PreferencesTab;
